import React, { useEffect, useState } from 'react'
import { Helmet } from 'react-helmet'
import { useDispatch, useSelector } from 'react-redux'
import { useLocation, useNavigate, useParams } from 'react-router-dom'
import { isPhotoPost, mediaPath, openPostState } from '../utils/mediaRoute'
import { BiImage } from 'react-icons/bi'
import { FaRegComment } from 'react-icons/fa'
import { FiBarChart2, FiFile, FiHeart, FiVideo } from 'react-icons/fi'
import FriendPart from '../comps/friendPart'
import Headerphoto from '../comps/headerphoto'
import UserAvatar from '../comps/userAvatar'
import ChatGifPane from '../comps/chatGifPane'
import { ChatFileCard } from '../comps/chatMediaBubbles'
import CommentThread from '../comps/commentThread'
import CommentComposer from '../comps/commentComposer'
import { useAuthGate } from '../comps/authPrompt'
import * as authAction from '../store/authAction'
import * as likeActions from '../data/like_data'
import * as normalActions from '../store/actions'
import { editCommunity, fetchCommunity, joinCommunity, joinRuleText, kickCommunityMember, rememberMine, rememberedMine, shareToCommunity, startCommunityStream, voteCommunityJoin } from '../data/communities_data'
import { firstPostImage, parsePostImages, uploadPostFile, uploadUrl } from '../utils/postImages'
import '../styles/community.css'

const fileType = (file) => {
  const mime = String(file && file.type || '')
  const name = String(file && file.name || '').toLowerCase()
  if (mime.startsWith('video/') || /\.(mp4|webm|mov|m4v)$/.test(name)) return 'l'
  if (mime.startsWith('audio/') || /\.(mp3|wav|m4a|aac)$/.test(name)) return 'a'
  return 'i'
}

const displayKind = (name, type) => {
  const n = String(name || '').toLowerCase()
  if (/\.(mp4|webm|mov|m4v)(\?|$)/.test(n) || type === 'l' || type === 's') return 'video'
  if (/\.(mp3|wav|m4a|aac)(\?|$)/.test(n) || type === 'a') return 'audio'
  if (/\.gif(\?|$)/.test(n) || /giphy\.com|tenor\.com/.test(n)) return 'gif'
  if (/\.(jpe?g|png|webp|avif|heic|gif)(\?|$)/.test(n) || /^https?:\/\//.test(String(name || ''))) return 'image'
  return 'file'
}

const Community = () => {
  const { id } = useParams()
  const navigate = useNavigate()
  const location = useLocation()
  const dispatch = useDispatch()
  const me = useSelector((state) => state.authReduse)
  const Comments = useSelector((state) => state.CommentsReduse.Comments)
  const { requireAuth, promptEl } = useAuthGate()
  const [data, setData] = useState(null)
  const [error, setError] = useState('')
  const [busy, setBusy] = useState('')
  const [title, setTitle] = useState('')
  const [file, setFile] = useState(null)
  const [gifUrl, setGifUrl] = useState('')
  const [gifOpen, setGifOpen] = useState(false)
  const [loading, setLoading] = useState(true)
  const [openComments, setOpenComments] = useState('')
  const [replyTo, setReplyTo] = useState(null)
  const [editing, setEditing] = useState(false)
  const [editName, setEditName] = useState('')
  const [editBio, setEditBio] = useState('')
  const [editAvatar, setEditAvatar] = useState(null)
  const [editBanner, setEditBanner] = useState(null)

  const load = async (userid) => {
    const next = await fetchCommunity(id, userid || me.id)
    setData(next)
    return next
  }

  useEffect(() => {
    let alive = true
    const boot = async () => {
      setLoading(true)
      try {
        const userData = localStorage.getItem('userdata')
        let userid = ''
        if (userData) {
          const parsed = JSON.parse(userData)
          userid = parsed.id
          await dispatch(authAction.auth(parsed.id, parsed.pid))
        }
        let next
        for (let attempt = 0; attempt < 3; attempt += 1) {
          try {
            next = await fetchCommunity(id, userid)
            break
          } catch (err) {
            if (attempt === 2 || /not found/i.test(err.message || '')) throw err
            await new Promise((resolve) => setTimeout(resolve, 1200))
          }
        }
        if (alive) setData(next)
      } catch (err) {
        if (alive) setError(err.message)
      } finally {
        if (alive) setLoading(false)
      }
    }
    boot()
    return () => { alive = false }
  }, [dispatch, id])

  const community = data && data.community
  const storedMine = rememberedMine((community && community.id) || id, me.id)
  // The server is the truth. The saved answer is only used until the page has loaded.
  const mine = data ? (data.mine || '') : storedMine
  const inGroup = mine === 'm' || mine === 'a'
  const waiting = mine === 'p'
  const isOwner = Boolean(community && me.id && String(community.ownerid) === String(me.id))
  const isAdmin = isOwner || mine === 'a'
  const posts = (data && data.posts) || []
  const feed = posts.map((row) => ({
    ...row,
    email: community && community.name,
    userImg: community && community.img,
    communityid: community && community.id,
    views: row.vCount,
    vlikes: row.pCount,
    vcomments: row.comm,
    date: row.time,
    description: row.discrption
  }))
  const formatCount = (value) => {
    const n = Number(value)
    if (!Number.isFinite(n) || n < 0) return 0
    if (n >= 1000000) return `${(n / 1000000).toFixed(1).replace(/\.0$/, '')}M`
    if (n >= 1000) return `${(n / 1000).toFixed(1).replace(/\.0$/, '')}K`
    return n
  }

  const onJoin = async () => {
    if (!requireAuth()) return
    if (busy === 'join') return
    setBusy('join')
    setError('')
    try {
      const result = await joinCommunity(community && community.id ? community.id : id, { userid: me.id, pid: me.pid })
      const nextMine = result.mine || 'p'
      rememberMine(community && community.id ? community.id : id, me.id, nextMine)
      setData((current) => current ? { ...current, mine: nextMine } : current)
      if (nextMine === 'a' || nextMine === 'm') await load()
    } catch (err) {
      setError(err.message)
    } finally {
      setBusy('')
    }
  }

  const openEdit = () => {
    setEditName((community && community.name) || '')
    setEditBio((community && community.bio) || '')
    setEditAvatar(null)
    setEditBanner(null)
    setError('')
    setEditing(true)
  }

  const onSaveEdit = async (event) => {
    event.preventDefault()
    if (!requireAuth() || !community) return
    setBusy('edit')
    setError('')
    try {
      const fields = { name: editName, bio: editBio }
      if (editAvatar) {
        fields.img = await uploadPostFile(editAvatar, 0)
        // A new profile picture also becomes the banner when there is no banner yet.
        if (!community.big_img) fields.big_img = fields.img
      }
      if (editBanner) fields.big_img = await uploadPostFile(editBanner, 0)
      await editCommunity(community.id, fields)
      setEditing(false)
      await load()
    } catch (err) {
      setError(err.message)
    } finally {
      setBusy('')
    }
  }

  const onKick = async (member) => {
    if (!requireAuth() || !community) return
    const person = (data.members || []).find((row) => String(row.userid) === String(member))
    const label = (person && (person.first_name || person.email)) || 'this member'
    if (!window.confirm(`Remove ${label} from ${community.name}?`)) return
    setBusy(member)
    setError('')
    try {
      await kickCommunityMember(community.id, member)
      await load()
    } catch (err) {
      setError(err.message)
    } finally {
      setBusy('')
    }
  }

  const onVote = async (applicant, accept) => {
    if (!requireAuth()) return
    setBusy(applicant)
    setError('')
    try {
      await voteCommunityJoin(community && community.id ? community.id : id, applicant, accept)
      await load()
    } catch (err) {
      setError(err.message)
    } finally {
      setBusy('')
    }
  }

  const onShare = async (event) => {
    event.preventDefault()
    if (!requireAuth()) return
    if (!file && !gifUrl) return
    setBusy('share')
    setError('')
    try {
      const saved = gifUrl || await uploadPostFile(file, 0)
      const kind = gifUrl ? 'i' : fileType(file)
      const thumb = String(saved).slice(0, 100)
      await shareToCommunity(id, {
        title: title.trim() || (file && file.name.slice(0, 40)) || 'GIF',
        discption: title.trim() || (file ? `Shared ${file.name}` : 'GIF'),
        image: saved,
        thumbnail: thumb,
        type: kind
      })
      setTitle('')
      setFile(null)
      setGifUrl('')
      setGifOpen(false)
      await load()
    } catch (err) {
      setError(err.message)
    } finally {
      setBusy('')
    }
  }

  const onStream = async () => {
    if (!requireAuth()) return
    setBusy('stream')
    setError('')
    try {
      const room = await startCommunityStream(id)
      navigate(`/rooms/${room.id}`)
    } catch (err) {
      setError(err.message)
    } finally {
      setBusy('')
    }
  }

  const openPostComments = async (postId) => {
    if (openComments === postId) {
      setOpenComments('')
      setReplyTo(null)
      return
    }
    setOpenComments(postId)
    setReplyTo(null)
    try {
      await dispatch(likeActions.get_post(postId))
    } catch (_) {}
  }

  const threadComments = Comments.filter((row) => String(row.postid) === String(openComments))
  const repliesByParent = threadComments.reduce((map, comment) => {
    const parentKey = comment.parentId ? String(comment.parentId) : ''
    if (!parentKey) return map
    map[parentKey] = map[parentKey] || []
    map[parentKey].push(comment)
    return map
  }, {})
  const rootComments = threadComments.filter((comment) => !comment.parentId)

  return (
    <section className="homePage visitedHomepage communityProfile communityGlass">
      {promptEl}
      <Helmet>
        <title>{community ? community.name : 'Community'}</title>
        <meta name="theme-color" content="#000000" />
      </Helmet>
      <FriendPart />
      <Headerphoto name={me.name} img={community && community.big_img} />
      {error && <p className="communityError">{error}</p>}
      {loading && !data && <p className="communityPendingLabel">Loading community…</p>}
      <div className="communityProfileCard">
        <UserAvatar className="communityAvatar" src={community && community.img} name={community && community.name} />
        <div>
          <h1>{community ? community.name : 'Community'}</h1>
          <p>{community && community.bio}</p>
          <p className="communityMeta">{community ? community.memberCount : 0} members</p>
          {community && community.joinRule ? <p className="communityMeta">{joinRuleText(community.joinRule)}</p> : null}
          <div className="communityActions">
            {!loading && data && !inGroup && !waiting && (
              <button type="button" onClick={onJoin} disabled={busy === 'join'}>{busy === 'join' ? 'Sending…' : 'Join'}</button>
            )}
            {waiting && (
              <button type="button" disabled>Request sent</button>
            )}
            {inGroup && (
              <button type="button" onClick={onStream} disabled={busy === 'stream'}>{busy === 'stream' ? 'Starting…' : 'Stream'}</button>
            )}
            {isOwner && (
              <button type="button" onClick={openEdit}>Edit</button>
            )}
          </div>
        </div>
      </div>

      <div className="communityMembers xMembers">
        <h2>Members</h2>
        <div className="communityMemberRow">
          {((data && data.members) || []).map((person) => {
            const canKick = isAdmin && String(person.userid) !== String(community && community.ownerid) && String(person.userid) !== String(me.id)
            return (
              <div key={person.userid} className="communityMemberItem">
                <button type="button" className="communityMemberChip" onClick={() => navigate(`/@${person.userid}`)} title={person.email || person.first_name}>
                  <UserAvatar src={person.img} name={person.email || person.first_name} />
                </button>
                <span className="communityMemberName">{person.first_name || person.email || person.userid}</span>
                {canKick && (
                  <button type="button" className="communityKick" disabled={busy === person.userid} onClick={() => onKick(person.userid)}>
                    {busy === person.userid ? 'Removing…' : 'Kick'}
                  </button>
                )}
              </div>
            )
          })}
        </div>
      </div>

      {inGroup && (data.pending || []).length > 0 && (
        <div className="communityPending">
          <h2>Join requests</h2>
          {data.pending.map((person) => (
            <div key={person.userid} className="communityPendingRow">
              <UserAvatar src={person.img} name={person.email} />
              <span>{person.email || person.userid}</span>
              <button type="button" disabled={busy === person.userid} onClick={() => onVote(person.userid, true)}>
                {person.need > 1 ? `Accept ${person.yes || 0}/${person.need}` : 'Accept'}
              </button>
              <button type="button" disabled={busy === person.userid} onClick={() => onVote(person.userid, false)}>Decline</button>
            </div>
          ))}
        </div>
      )}

      {waiting && <p className="communityPendingLabel">{joinRuleText(community && community.joinRule)}</p>}

      {editing && (
        <div className="communityModal" onClick={() => setEditing(false)} role="presentation">
          <form className="communityModalCard" onClick={(event) => event.stopPropagation()} onSubmit={onSaveEdit}>
            <h2>Edit community</h2>
            <label>
              Name
              <input value={editName} onChange={(event) => setEditName(event.target.value)} maxLength={40} minLength={3} required />
            </label>
            <label>
              Bio
              <textarea value={editBio} onChange={(event) => setEditBio(event.target.value)} maxLength={400} rows={3} />
            </label>
            <label className="communityPhotoPick">
              Photo {editAvatar ? `: ${editAvatar.name}` : ''}
              <input type="file" accept="image/*" onChange={(event) => setEditAvatar(event.target.files && event.target.files[0])} />
            </label>
            <label className="communityPhotoPick">
              Banner {editBanner ? `: ${editBanner.name}` : ''}
              <input type="file" accept="image/*" onChange={(event) => setEditBanner(event.target.files && event.target.files[0])} />
            </label>
            {error && <p className="communityError">{error}</p>}
            <div className="communityModalActions">
              <button type="button" onClick={() => setEditing(false)}>Cancel</button>
              <button type="submit" disabled={busy === 'edit'}>{busy === 'edit' ? 'Saving…' : 'Save'}</button>
            </div>
          </form>
        </div>
      )}

      <div className="xFeed">
        {inGroup && (
          <form className="xCompose" onSubmit={onShare}>
            <UserAvatar src={community && community.img} name={community && community.name} />
            <div className="xComposeBody">
              <input value={title} onChange={(event) => setTitle(event.target.value)} placeholder="What's happening?" maxLength={60} />
              {gifUrl ? <img className="xComposePreview" src={gifUrl} alt="" /> : null}
              {file && String(file.type || '').startsWith('video') ? <video className="xComposePreview" src={URL.createObjectURL(file)} controls playsInline /> : null}
              {file && String(file.type || '').startsWith('image') ? <img className="xComposePreview" src={URL.createObjectURL(file)} alt="" /> : null}
              {file && !String(file.type || '').startsWith('video') && !String(file.type || '').startsWith('image') ? <em className="xComposeFile">{file.name}</em> : null}
              <div className="xComposeBar">
                <div className="xComposeTools">
                  <label className="xComposeMedia" title="Photo">
                    <BiImage />
                    <input type="file" accept="image/*" onChange={(event) => { setGifUrl(''); setFile(event.target.files && event.target.files[0]); }} />
                  </label>
                  <label className="xComposeMedia" title="Video">
                    <FiVideo />
                    <input type="file" accept="video/*" onChange={(event) => { setGifUrl(''); setFile(event.target.files && event.target.files[0]); }} />
                  </label>
                  <label className="xComposeMedia" title="File">
                    <FiFile />
                    <input type="file" accept=".pdf,.zip,.txt,.gif,image/gif,audio/*" onChange={(event) => { setGifUrl(''); setFile(event.target.files && event.target.files[0]); }} />
                  </label>
                  <button type="button" className={`xComposeGif${gifOpen ? ' isOn' : ''}`} onClick={() => setGifOpen((open) => !open)}>GIF</button>
                </div>
                <button type="submit" disabled={busy === 'share' || (!file && !gifUrl)}>{busy === 'share' ? 'Posting…' : 'Post'}</button>
              </div>
              <ChatGifPane open={gifOpen} onPick={(url) => { setFile(null); setGifUrl(url); setGifOpen(false); }} />
            </div>
          </form>
        )}

        {!loading && feed.length === 0 && <p className="xFeedEmpty">No posts yet</p>}
        {feed.map((item, index) => {
          const files = parsePostImages(item.image)
          const media = firstPostImage(item.image) || item.thumbnail
          const kind = displayKind(media, item.type)
          const src = uploadUrl(media)
          const commentsOpen = openComments === item.id
          return (
            <article key={item.id || index} className="xTweet">
              <div className="xTweetRail">
                <UserAvatar src={item.userImg} name={item.email} />
              </div>
              <div className="xTweetMain">
                <div className="xTweetHead">
                  <strong>{item.email}</strong>
                  <span>· {item.date}</span>
                </div>
                {item.title ? <p className="xTweetText">{item.title}</p> : null}
                {item.description && item.description !== item.title ? <p className="xTweetText dim">{item.description}</p> : null}
                {media && (kind === 'video') ? (
                  <div className="xTweetMedia">
                    <video src={src} controls playsInline preload="metadata" />
                  </div>
                ) : null}
                {media && (kind === 'image' || kind === 'gif') ? (
                  <div
                    className="xTweetMedia"
                    role="button"
                    tabIndex={0}
                    onClick={() => {
                      const photos = feed.filter(post => isPhotoPost(post) || post.type === 'i')
                      const at = photos.findIndex(post => String(post.id) === String(item.id))
                      const rest = at < 0 ? [item] : photos.slice(at)
                      navigate(mediaPath(item), { state: openPostState(item, location, { feed: rest, source: 'list' }) })
                    }}
                  >
                    <img src={src} alt={item.title || ''} />
                    {files.length > 1 ? <b className="xTweetStack">{files.length}</b> : null}
                  </div>
                ) : null}
                {media && kind === 'audio' ? (
                  <audio className="xTweetAudio" src={src} controls />
                ) : null}
                {media && kind === 'file' ? (
                  <div onClick={(event) => event.stopPropagation()}>
                    <ChatFileCard src={src} label={media.split('/').pop()} />
                  </div>
                ) : null}
                <div className="xTweetActions">
                  <button type="button" onClick={() => openPostComments(item.id)}>
                    <FaRegComment /> {formatCount(item.vcomments)}
                  </button>
                  <span><FiHeart /> {formatCount(item.vlikes)}</span>
                  <span><FiBarChart2 /> {formatCount(item.views)}</span>
                </div>
                {commentsOpen ? (
                  <div className="xTweetComments">
                    {rootComments.map((comment) => (
                      <CommentThread
                        key={comment.id}
                        comment={comment}
                        repliesByParent={repliesByParent}
                        onReply={setReplyTo}
                        onVote={(type, commentId) => dispatch(normalActions.add_to_comupds(type, me.id, commentId))}
                        onDelete={(row) => dispatch(normalActions.delete_comment(row.id, me.id, me.pid))}
                        canDelete={(row) => String(row.userid) === String(me.id)}
                      />
                    ))}
                    <CommentComposer
                      replyTo={replyTo}
                      onCancelReply={() => setReplyTo(null)}
                      userPhoto={me.photo}
                      userEmail={me.email}
                      onFocusAuth={requireAuth}
                      onSubmit={async ({ text, media }) => {
                        if (!requireAuth()) return
                        await dispatch(normalActions.add_to_comments(text, me.id, item.id, me.photo, me.email, me.pid, replyTo && replyTo.id, media))
                        setReplyTo(null)
                      }}
                    />
                  </div>
                ) : null}
              </div>
            </article>
          )
        })}
      </div>
    </section>
  )
}

export default Community
